import React, { useState, useCallback } from 'react';
import { View, Text, TextInput } from 'react-native';
import i18n from 'i18n-js';
import TouchableElement from '@components/common/touchableElement';
import Svg from '@styles/icons/svg';
import EditableLine from '@components/common/editableLine';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { StepLinkToPartProps, PartData, TouchableRenderProps, PartSearchItem, AutocompleteItem } from '@components/common/NCFileType/NCFileWizardNoERP/types';
import { INITIAL_PART_DATA, PART_MODE_OPTIONS } from '@components/common/NCFileType/NCFileWizardNoERP/constants';
import { partFields } from '@components/productionInformation/config';

let searchTimeout: ReturnType<typeof setTimeout> | null = null;
let loadPartTimeout: ReturnType<typeof setTimeout> | null = null;
let loadDrawingTimeout: ReturnType<typeof setTimeout> | null = null;

const StepLinkToPart: React.FC<StepLinkToPartProps> = ({
  partMode,
  setPartMode,
  partData,
  setPartData,
  searchQuery,
  setSearchQuery,
  searchResults,
  searchLoading,
  onSearch,
  onPartsAutocomplete,
  C,
  scale,
  font,
}) => {
  const [isNew, setIsNew] = useState(false);
  const [showNewButton, setShowNewButton] = useState(!partData.partId);
  const [autocompleteList, setAutocompleteList] = useState<Record<string, AutocompleteItem[]>>({
    partNumber: [],
    partDrawingNumber: [],
  });
  const [loadingAutocomplete, setLoadingAutocomplete] = useState<Record<string, boolean>>({
    partNumber: false,
    partDrawingNumber: false,
  });

  const autocomplete: Record<string, boolean> = {
    partNumber: true,
    partDrawingNumber: true,
  };

  const loadParts = useCallback(
    async (value: string) => {
      if (!value) return;
      const list = await onPartsAutocomplete(value);
      setAutocompleteList((prev) => ({
        ...prev,
        partNumber: (list?.searchResult || []).map((i: PartSearchItem) => ({
          id: i.partId,
          topLine: i.partName,
          middleLine: i.description,
          bottomLine: i.drawing,
          ...i,
        })),
      }));
      setLoadingAutocomplete((prev) => ({ ...prev, partNumber: false }));
    },
    [onPartsAutocomplete],
  );

  const loadDrawing = useCallback(
    async (value: string) => {
      if (!value) return;
      const list = await onPartsAutocomplete(value);
      setAutocompleteList((prev) => ({
        ...prev,
        partDrawingNumber: (list?.searchResult || []).map((i: PartSearchItem) => ({
          id: i.partId,
          topLine: i.drawing,
          middleLine: i.description,
          bottomLine: i.partName,
          ...i,
        })),
      }));
      setLoadingAutocomplete((prev) => ({ ...prev, partDrawingNumber: false }));
    },
    [onPartsAutocomplete],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
      if (searchTimeout) clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        onSearch(value);
      }, 500);
    },
    [onSearch, setSearchQuery],
  );

  const handleSelectPart = useCallback(
    (part: PartSearchItem) => {
      if (partData.partId === String(part.partId)) {
        setPartData({ ...INITIAL_PART_DATA });
        return;
      }
      setPartData({
        partId: String(part.partId),
        partNumber: part.partName || '',
        partDrawingNumber: part.drawing || '',
        partDrawingRevision: part.revision || '',
        partDrawingPosition: part.position || '',
        partDescription: part.description || '',
      });
    },
    [setPartData, partData.partId],
  );

  const handleNewPartFieldChange = useCallback(
    (key: keyof PartData, value: string) => {
      setPartData({
        ...partData,
        partId: null,
        [key]: value,
      });

      if (loadPartTimeout) clearTimeout(loadPartTimeout);
      if (loadDrawingTimeout) clearTimeout(loadDrawingTimeout);

      if (key === 'partNumber') {
        setShowNewButton(true);
        setLoadingAutocomplete((prev) => ({ ...prev, partNumber: true }));
        loadPartTimeout = setTimeout(() => {
          loadParts(value);
        }, 1000);
      }
      if (key === 'partDrawingNumber') {
        setShowNewButton(true);
        setLoadingAutocomplete((prev) => ({ ...prev, partDrawingNumber: true }));
        loadDrawingTimeout = setTimeout(() => {
          loadDrawing(value);
        }, 1000);
      }
    },
    [partData, setPartData, loadParts, loadDrawing],
  );

  const handleAutocompletePress = useCallback(
    (item: AutocompleteItem) => {
      setIsNew(false);
      setShowNewButton(false);
      setPartData({
        partId: String(item.partId),
        partNumber: item.partName || '',
        partDrawingNumber: item.drawing || '',
        partDrawingRevision: item.revision || '',
        partDrawingPosition: item.position || '',
        partDescription: item.description || '',
      });
      setAutocompleteList({ partNumber: [], partDrawingNumber: [] });
    },
    [setPartData],
  );

  const handleNewPress = useCallback(() => {
    setIsNew(true);
    setPartData({ ...INITIAL_PART_DATA });
  }, [setPartData]);

  return (
    <View style={{ paddingHorizontal: scale(15) }}>
      {/* Tab toggle */}
      <View
        style={{
          flexDirection: 'row',
          backgroundColor: C('Background/Light/Field lighter'),
          borderRadius: scale(6),
          padding: scale(2),
          marginBottom: scale(16),
        }}
      >
        {PART_MODE_OPTIONS.map((tab) => {
          const isActive = partMode === tab.key;
          return (
            <TouchableElement key={tab.key} onPress={() => setPartMode(tab.key)} hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }}>
              {({ boxStyle }: TouchableRenderProps) => (
                <View
                  style={[
                    boxStyle,
                    {
                      flex: 1,
                      paddingVertical: scale(6),
                      paddingHorizontal: scale(12),
                      borderRadius: scale(5),
                      backgroundColor: isActive ? C('Gray/Light/5') : C('Background/Light/Panel'),
                      alignItems: 'center',
                    },
                  ]}
                >
                  <Text style={[font['Subhead 1'], { color: isActive ? C('Label/Light/Primary') : C('Label/Light/Tertiary') }]}>
                    {i18n.t(tab.label)}
                  </Text>
                </View>
              )}
            </TouchableElement>
          );
        })}
      </View>

      {partMode === 'existing' ? (
        <View>
          {/* Search input */}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              backgroundColor: C('Gray/Light/5'),
              borderRadius: scale(SCREEN_BORDER_RADIUS),
              paddingHorizontal: scale(12),
              marginBottom: scale(12),
            }}
          >
            <Svg icon="search" width={scale(16)} height={scale(16)} color="Label/Light/Tertiary" />
            <TextInput
              style={[
                font.Body,
                {
                  flex: 1,
                  color: C('Label/Light/Primary'),
                  paddingVertical: scale(10),
                  paddingHorizontal: scale(8),
                  outlineStyle: 'none',
                },
              ]}
              placeholder={i18n.t('Search parts...')}
              placeholderTextColor={C('Label/Light/Tertiary')}
              value={searchQuery}
              onChangeText={handleSearchChange}
              autoCorrect={false}
            />
          </View>

          {/* Results */}
          <View>
            {searchLoading && (
              <View style={{ padding: scale(20), alignItems: 'center' }}>
                <Text style={[font.Footnote, { color: C('Label/Light/Tertiary') }]}>{i18n.t('Searching...')}</Text>
              </View>
            )}
            {!searchLoading && searchResults.length === 0 && searchQuery.length > 0 && (
              <View style={{ padding: scale(20), alignItems: 'center' }}>
                <Text style={[font.Footnote, { color: C('Label/Light/Tertiary') }]}>{i18n.t('No parts found')}</Text>
              </View>
            )}
            {searchResults.map((part) => {
              const isSelected = partData.partId === String(part.partId);
              return (
                <TouchableElement key={part.partId} onPress={() => handleSelectPart(part)} hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }}>
                  {({ boxStyle }: TouchableRenderProps) => (
                    <View
                      style={[
                        boxStyle,
                        {
                          flexDirection: 'row',
                          alignItems: 'center',
                          padding: scale(12),
                          marginBottom: scale(6),
                          borderRadius: scale(SCREEN_BORDER_RADIUS),
                          borderWidth: scale(2),
                          borderColor: isSelected ? C('Selected/Light/Primary') : C('Hairline/Light/No trans'),
                          backgroundColor: C('Background/Light/Field lighter'),
                        },
                      ]}
                    >
                      <View style={{ flex: 1 }}>
                        <Text style={[font['Subhead 1'], { color: C('Label/Light/Primary') }]}>
                          {part.partName || part.partNumber}
                        </Text>
                        {part.description ? (
                          <Text style={[font.Footnote, { color: C('Label/Light/Secondary'), marginTop: scale(2) }]}>{part.description}</Text>
                        ) : null}
                        {part.revision ? (
                          <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary'), marginTop: scale(2) }]}>
                            {i18n.t('Rev')}: {part.revision}
                          </Text>
                        ) : null}
                      </View>
                      <Svg
                        icon={isSelected ? 'selected' : 'unselected'}
                        width={scale(22)}
                        height={scale(22)}
                        color={isSelected ? 'Selected / Light / Primary' : 'Label/Light/Quinary'}
                      />
                    </View>
                  )}
                </TouchableElement>
              );
            })}
          </View>
        </View>
      ) : (
        <View>
          {/* Create New Part form using EditableLine */}
          <View style={{ borderRadius: scale(SCREEN_BORDER_RADIUS), zIndex: 1000 }}>
            {partFields.map((field, index) => (
              <EditableLine
                key={field.name}
                {...{
                  scale,
                  font,
                  item: {
                    value: partData[field.key as keyof PartData] || '',
                    onChange: (v: string) => handleNewPartFieldChange(field.key as keyof PartData, v),
                    showNewButton: showNewButton && autocomplete[field.key],
                    onNewPress: handleNewPress,
                    autocompleteLoading: loadingAutocomplete[field.key],
                    autocomplete: autocomplete[field.key] && !isNew,
                    autocompleteList: autocompleteList[field.key],
                    onAutocompletePress: handleAutocompletePress,
                    clearButtonMode: 'never',
                    ...field,
                  },
                  index,
                  array: partFields,
                  C,
                  bottomBorderBox: false,
                  topBorder: index !== 0,
                  roundTop: index === 0,
                  roundBottom: index === partFields.length - 1,
                }}
              />
            ))}
          </View>
          {!partData.partId && (partData.partNumber || partData.partDrawingNumber) && (
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                backgroundColor: C('Status/Light/Active'),
                borderRadius: scale(SCREEN_BORDER_RADIUS),
                padding: scale(12),
                marginTop: scale(12),
              }}
            >
              <Svg icon="check" width={scale(16)} height={scale(16)} color="Label/Light/AlwaysLight Primary" />
              <Text style={[font.Footnote, { color: C('Label/Light/AlwaysLight Primary'), marginLeft: scale(8) }]}>
                {i18n.t('New part will be created')}
              </Text>
            </View>
          )}
          <View>
            <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary'), paddingHorizontal: scale(16), marginTop: scale(6) }]}>
              {i18n.t('If you add Part Information, either the field Part or Drawing must be filled')}
            </Text>
          </View>
        </View>
      )}

      <View style={{ paddingTop: scale(8) }}>
        <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary') }]}>
          {i18n.t('You can skip this step and link a part later.')}
        </Text>
      </View>
    </View>
  );
};

export default StepLinkToPart;
